import { createFileRoute, Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { z } from "zod";
import { ImportPage } from "../components/imports/ImportPage";
import { ArchivedNotice, ManagedProjectGate } from "../components/projects/ProjectStates";
import { Skeleton } from "../components/ui/skeleton";
import { useProject } from "../lib/projects";
import { strings } from "../lib/strings";
import { PageTitle } from "../lib/title";

const words = strings.imports;

/** The import open, by its id in the address, so a reload or a link comes back to it. */
const importSearchSchema = z.object({ id: z.string().max(40).optional().catch(undefined) });

/** A project's CSV imports (BUILD_SPEC §19.1), for those who configure it: its admins and Steer's Administrators. */
function ImportRoute() {
  const { projectKey } = useParams({ from: "/admin/p/$projectKey/import" });
  const search = useSearch({ from: "/admin/p/$projectKey/import" });
  const navigate = useNavigate();
  const project = useProject(projectKey);
  return (
    <ManagedProjectGate
      project={project}
      what={words.manage}
      skeleton={
        <div className="mx-auto max-w-5xl space-y-3 px-6 py-8">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-40 w-full" />
        </div>
      }
    >
      {(detail) => (
        <div className="mx-auto max-w-5xl space-y-6 px-6 py-8">
          <PageTitle name={`${words.title} · ${detail.project.name}`} />
          <header className="flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))]">
            <Link to="/admin/p/$projectKey" params={{ projectKey: detail.project.key }} className="hover:underline">
              {detail.project.name}
            </Link>
            <span>·</span>
            <Link
              to="/admin/p/$projectKey/settings"
              params={{ projectKey: detail.project.key }}
              className="hover:underline"
            >
              {strings.projects.settings.title}
            </Link>
            <span>·</span>
            <h1 className="text-[hsl(var(--foreground))]">{words.title}</h1>
          </header>
          <ArchivedNotice project={detail.project} canManage={detail.canManage} />
          <ImportPage
            detail={detail}
            openId={search.id}
            onOpen={(id) =>
              void navigate({ to: "/admin/p/$projectKey/import", params: { projectKey }, search: { id } })
            }
          />
        </div>
      )}
    </ManagedProjectGate>
  );
}

export const Route = createFileRoute("/admin/p/$projectKey/import")({
  validateSearch: importSearchSchema,
  component: ImportRoute,
});
